import {
  Comparator,
  NgRedux,
  PathSelector,
  Selector,
} from '@angular-redux/store';
import { Injectable } from '@angular/core';

import * as fp from 'lodash/fp';
import { Observable, rx } from '@shared/rx';
import { IAppState } from '@store/index';

import {
  ICompany,
  ENTITY_TYPES,
  ObjectId,
  SchemaNode,
  GRID_LIST_VIEW_TYPE,
  STAND_WORKSHEET_ENTRY_FORM_VIEW_TYPE,
  STAND_WORKSHEET_GLOBAL_PRICE_SETTING,
  DURING_EVENT_TRANSFER_AUTO_COMPLETE_SETTING,
  IUserConfig,
  USER_CONFIG_MODULE_NAME,
  USER_CONFIG_MODULE_TYPE,
} from '@shared/types';
import { STATE_SELECTORS } from './state-selectors';
import {
  Dictionary,
  find,
  get,
  isEmpty,
  isFunction,
  isNumber,
  isUndefined,
  pick,
  values,
} from 'lodash';
import {
  DEFAULT_DECIMAL_DIVIDER,
  DEFAULT_NUMBER_PRECISION,
  DEFAULT_THOUSANDS_DIVIDER,
  ENTITY_ID_PATH,
  DEFAULT_CURRENCY_CODE,
  DEFAULT_NEGATIVES,
  DEFAULT_CURRENCY_SYMBOL,
  DEFAULT_LOCALE,
} from '@config/index';
import { environment } from '../../../../environments/environment';
import { ILocales } from '@app/i18n/i18n.types';

@Injectable()
export class StateService {
  public static getByPath(
    state: IAppState,
    path: PathSelector | string,
    defaultValue?: any,
  ): any {
    return get(state, path, defaultValue);
  }

  public static getBySelector<T = any>(
    state: IAppState,
    selector: Selector<IAppState, T>,
    defaultValue?: T,
  ): T {
    if (isFunction(selector)) {
      return selector(state);
    }

    return get(state, selector, defaultValue);
  }

  public static getCurrentCompanyId(state: IAppState): string {
    return StateService.getBySelector(
      state,
      STATE_SELECTORS.CURRENT_COMPANY_ID,
    );
  }

  public static getCurrentCompany(state: IAppState): ICompany {
    return StateService.getBySelector(state, STATE_SELECTORS.CURRENT_COMPANY);
  }

  public static getUiListEntityType(state: IAppState): string {
    return StateService.getBySelector(
      state,
      STATE_SELECTORS.UI_LIST_ENTITY_TYPE,
    );
  }

  public static getEntityItems(state: IAppState, entityType: string): string[] {
    return StateService.getBySelector(
      state,
      ['entities', entityType, 'items'],
      [],
    );
  }

  public static getCurrentEntityType(state: IAppState): ENTITY_TYPES {
    return StateService.getBySelector(
      state,
      STATE_SELECTORS.CURRENT_ENTITY_TYPE,
    );
  }

  public static isDeleteViewMode(state: IAppState): boolean {
    return StateService.getBySelector(
      state,
      STATE_SELECTORS.UI_DELETE_VIEW_MODE,
    );
  }

  public static getState(): IAppState {
    return StateService.getState();
  }

  public constructor(private ngRedux: NgRedux<IAppState>) {}

  public getState(): IAppState {
    return this.ngRedux.getState();
  }

  public getByPath<T = any>(path: PathSelector | string, defaultValue?: T): T {
    return StateService.getByPath(this.getState(), path, defaultValue);
  }

  public getBySelector<T = any>(
    selector: Selector<IAppState, T>,
    defaultValue?: T,
  ): T {
    return StateService.getBySelector(this.getState(), selector, defaultValue);
  }

  public select<S>(
    selector?: Selector<IAppState, S>,
    comparator?: Comparator,
  ): Observable<S> {
    return this.ngRedux.select(selector, comparator);
  }

  public selectEntityItems<R extends object>(
    entityType: ENTITY_TYPES,
  ): Observable<R[]> {
    return this.select(['entities', entityType, 'items']);
  }

  public selectCurrentEntity<R>(
    projection: string[] = [],
  ): Observable<Partial<R>> {
    const entityType = this.getCurrentEntityType();

    return this.select(['entities', entityType, 'currentDocument']).pipe(
      rx.map<any, Partial<R>>((item) => {
        if (isEmpty(projection)) {
          return item;
        }

        return pick(item, projection);
      }),
    );
  }

  public selectIndexedEntities<R extends { entity_id?: ObjectId }>(
    entityType: ENTITY_TYPES,
    indexKey: keyof R = ENTITY_ID_PATH,
  ): Observable<Dictionary<R>> {
    /* eslint-disable-next-line */
    return this.selectEntityItems(entityType).pipe(
      rx.filter(Boolean),
      rx.map(fp.keyBy(indexKey)),
      rx.shareReplay(1),
    ) as Observable<Dictionary<R>>;
  }

  public selectSchema<S>(entityType: ENTITY_TYPES): Observable<S> {
    return this.select<S>(['entities', entityType, 'schema']);
  }

  public getCurrentCompanyId(): string {
    return this.getBySelector(STATE_SELECTORS.CURRENT_COMPANY_ID);
  }

  public getCurrentUserName(): string {
    return this.getBySelector(STATE_SELECTORS.CURRENT_USER_NAME);
  }

  public getCurrentUserId(): string {
    return this.getBySelector(STATE_SELECTORS.CURRENT_USER_ID);
  }

  public getCurrentCompany(): ICompany {
    return StateService.getCurrentCompany(this.getState());
  }

  public getUiListEntityType(): string {
    return StateService.getUiListEntityType(this.getState());
  }

  public getEntityItems(entityType: string): any[] {
    return StateService.getEntityItems(this.getState(), entityType);
  }

  public isDeleteViewMode(): boolean {
    return StateService.isDeleteViewMode(this.getState());
  }

  public dispatch(action: any): any {
    return this.ngRedux.dispatch(action);
  }

  public getCurrentEntityType(): ENTITY_TYPES {
    return StateService.getCurrentEntityType(this.getState());
  }

  public getBufferedEntityItems(entityType: ENTITY_TYPES): Observable<any[]> {
    return this.select(
      STATE_SELECTORS.bufferedItemsByEntitySelector(entityType),
    );
  }

  public getEntityCaptionsStringListByIds(
    entityType: string,
    ids: string[],
  ): Observable<string> {
    return this.ngRedux.select(['entities', entityType, 'itemCaptions']).pipe(
      rx.map((entityCaptions = {}): string => {
        const captions = values(pick(entityCaptions, ids));

        return isEmpty(captions) ? '' : captions.join(', ');
      }),
    );
  }

  public getEntityItemCaptions(
    entityType: ENTITY_TYPES,
  ): Record<ObjectId, string> {
    return StateService.getByPath(this.getState(), [
      'entities',
      entityType,
      'itemCaptions',
    ]);
  }
  public getSchemaByEntityType(entityType: ENTITY_TYPES): SchemaNode {
    const selector = STATE_SELECTORS.schemaNodeSelector(entityType);

    return this.getBySelector(selector);
  }

  public getCurrencyPrecision(): number {
    const currencyPrecision: string = StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.XDM_CURRENCY_PRECISION,
    );

    return Number(
      isNumber(currencyPrecision)
        ? currencyPrecision
        : DEFAULT_NUMBER_PRECISION,
    );
  }

  public getNegatives(): string {
    const negatives: string = StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.XDM_NEGATIVES,
      DEFAULT_NEGATIVES,
    );

    return String(negatives);
  }

  public getCurrencyCode(): string {
    const currencySymbolCode: string = StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.XDM_CURRENCY_CODE,
      DEFAULT_CURRENCY_CODE,
    );

    return String(currencySymbolCode);
  }

  public getCurrencySymbol(): string {
    const currencySymbol: string = StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.XDM_CURRENCY_SYMBOL,
      DEFAULT_CURRENCY_SYMBOL,
    );

    return String(currencySymbol);
  }

  public getCurrencyThousandsSeparator(): string {
    const currencyThousandsSeparator: string = StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.XDM_THOUSANDS_SEPARATOR,
      DEFAULT_THOUSANDS_DIVIDER,
    );

    return String(currencyThousandsSeparator);
  }

  public getCurrencyDecimalSeparator(): string {
    const currencyDecimalSeparator: string = StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.XDM_DECIMAL_SEPARATOR,
      DEFAULT_DECIMAL_DIVIDER,
    );

    return String(currencyDecimalSeparator);
  }

  public getNumberPrecision(): number {
    return (
      Number(
        StateService.getByPath(
          this.getState(),
          STATE_SELECTORS.XVI_NUMBER_PRECISION,
        ),
      ) || DEFAULT_NUMBER_PRECISION
    );
  }

  public getTransferItemListViewSetting(): GRID_LIST_VIEW_TYPE {
    return (
      StateService.getByPath(
        this.getState(),
        STATE_SELECTORS.TRANSFER_ITEM_LIST_VIEW,
      ) || GRID_LIST_VIEW_TYPE.CATEGORY_VIEW
    );
  }

  public getTransferDisplayQohFrom(): boolean {
    const showQohFrom = StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.TRANSFER_DISPLAY_QOH_FROM,
    );

    return isUndefined(showQohFrom) ? true : showQohFrom;
  }

  public getOrderItemListViewSetting(): GRID_LIST_VIEW_TYPE {
    return (
      StateService.getByPath(
        this.getState(),
        STATE_SELECTORS.ORDER_ITEM_LIST_VIEW,
      ) || GRID_LIST_VIEW_TYPE.CATEGORY_VIEW
    );
  }

  public getStandWorksheetThreshold(path): number {
    return StateService.getByPath(
      this.getState(),
      STATE_SELECTORS[`XDM_STAND_WORKSHEET_${path}`],
    );
  }

  public getStandWorkSheetEntryFormSummaryViewSetting(): STAND_WORKSHEET_ENTRY_FORM_VIEW_TYPE {
    return StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.XDM_STAND_WORKSHEET_ENTRY_FORM_SUMMARY_VIEW,
      STAND_WORKSHEET_ENTRY_FORM_VIEW_TYPE.SUMMARY_VIEW,
    );
  }

  public getInventoryCountViewSetting(): GRID_LIST_VIEW_TYPE {
    return StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.INVENTORY_COUNT_LIST_VIEW,
      GRID_LIST_VIEW_TYPE.CATEGORY_VIEW,
    );
  }

  public getStandWorkSheetGlobalPriceSetting(): STAND_WORKSHEET_GLOBAL_PRICE_SETTING {
    return StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.XDM_STAND_WORKSHEET_GLOBAL_PRICE_SETTING,
      STAND_WORKSHEET_GLOBAL_PRICE_SETTING.HIGHEST_PARENT_PRODUCT_PRICE,
    );
  }

  public getDuringTransferAutoCompleteConfig(): DURING_EVENT_TRANSFER_AUTO_COMPLETE_SETTING {
    return StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.XDM_DURING_TRANSFER_AUTO_COMPLETE_SETTING,
      DURING_EVENT_TRANSFER_AUTO_COMPLETE_SETTING.NOT_AUTO_COMPLETE,
    );
  }

  public getStandWorkSheetDepartmentCodeSetting(
    departmentCode: string,
  ): boolean {
    return (
      StateService.getByPath(this.getState(), [
        ...STATE_SELECTORS.XDM_STAND_WORKSHEET_DEPARTMENT_CODE_SETTING,
        departmentCode,
      ]) ?? false
    );
  }

  public getStandWorksheetPostClosureSetting(): boolean {
    return StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.XDM_STAND_WORKSHEET_POST_CLOSURE_SETTING,
      true,
    );
  }

  public getUserConfig(
    moduleName: USER_CONFIG_MODULE_NAME,
    moduleType: USER_CONFIG_MODULE_TYPE,
  ): IUserConfig {
    const userConfig = StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.USER_CONFIG,
      [],
    );

    return find(userConfig, {
      module_name: moduleName,
      module_type: moduleType,
    });
  }

  public getPendingRequestTimeout(): number | null {
    const switchesForAlert: boolean = StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.PENDING_REQUEST_ALERT,
    );

    const timeout = StateService.getByPath(
      this.getState(),
      STATE_SELECTORS.PENDING_REQUEST_TIMEOUT,
    );

    return switchesForAlert && isNumber(timeout) ? timeout : null;
  }

  public getDictionaryLocals(): ILocales {
    const devAppLocale = get(environment, 'DEV_APP_LOCALE', '');
    const defaultLocale =
      devAppLocale !== 'undefined' && devAppLocale !== ''
        ? devAppLocale
        : DEFAULT_LOCALE;

    return this.getBySelector(STATE_SELECTORS.LOCALES, {
      companyLocale: defaultLocale,
      userLocale: defaultLocale,
    });
  }

  public getAutoRefreshToggle(widgetKey: string): boolean {
    const dashboardWidgetsConfig = this.getUserConfig(
      USER_CONFIG_MODULE_NAME.DASHBOARD_WIDGETS,
      USER_CONFIG_MODULE_TYPE.DASHBOARD,
    );

    return get(
      dashboardWidgetsConfig,
      ['module_config', widgetKey, 'auto_refresh'],
      false,
    );
  }
}
